/**
 * The bout's sounds that raise no WorldEvent: qinggong steps, the dive, the slide, the knock, armour, a full rage bar and the
 * lock routines. Once a frame (called from tickSfx) it reads the duel systems' state for the player, the bout's two fighters
 * and the locked foe, and plays on each change (sounds/duelMoves.ts). It only reads; nothing here touches the World.
 */
import { duelAirOf, duelArmorOf, duelKnockOf, duelLockOf, duelMoveOf, duelSlideOf, lockRoutineOf, slideOf, type Entity } from '@nine/shared';
import type { Effects } from '../render/effects';
import { ctxFor } from './game';

interface Seen {
  stepAt: number;
  dive: number;
  slide: boolean;
  striking: boolean;
  knock: string;
  armSoak: number;
  armBreak: number;
  tier: 'red' | 'yellow' | null;
  rageFull: boolean;
  beat: number;
  formLeft: number;
  formLen: number;
  landedAt: number;
}

const seen = new Map<number, Seen>();

/** True while a fighter is at the end of a dive or just landed one: its landing has its own sound (events.ts `land` skips move.land). */
export const diveLanding = (id: number): boolean => (seen.get(id)?.dive ?? 0) >= 0.85 || (seen.get(id)?.landedAt ?? -9) > lastTime - 0.3;
let lastTime = 0;

const blank = (): Seen => ({
  stepAt: -1,
  dive: 0,
  slide: false,
  striking: false,
  knock: '',
  armSoak: -1,
  armBreak: -1,
  tier: null,
  rageFull: false,
  beat: -1,
  formLeft: 0,
  formLen: 0,
  landedAt: -9,
});

const STEP_ID = { back: 'duel.step.back', side: 'duel.step.side', fwd: 'duel.step.fwd', air: 'duel.step.air', escape: 'duel.escape' } as const;
const KNOCK_ID: Record<string, string> = {
  air: 'duel.knock.fling',
  trip: 'duel.knock.trip',
  down: 'duel.knock.down',
  rise: 'duel.getup.rise',
  roll: 'duel.getup.roll',
  riseAttack: 'duel.getup.attack',
};
/** Heard from this far (tiles); the player's own are centred. */
const RANGE = 14;

export function tickDuelSfx(fx: Effects): void {
  const w = fx.world;
  lastTime = w.time;
  const p = w.player;
  const c = ctxFor(fx);
  const ids = new Set<number>([p.id]);
  const b = w.pvpBout;
  if (b) {
    ids.add(b.a);
    ids.add(b.b);
  }
  const locked = duelLockOf(w)?.lockedId();
  if (locked != null) ids.add(locked);
  const move = duelMoveOf(w);
  const air = duelAirOf(w);
  const slide = duelSlideOf(w);
  const knock = duelKnockOf(w);
  const armor = duelArmorOf(w);
  const lock = duelLockOf(w);
  for (const id of ids) {
    const e: Entity | undefined = w.get(id);
    if (!e) continue;
    if (e !== p && Math.hypot(e.x - p.x, e.y - p.y) > RANGE) continue;
    let s = seen.get(id);
    const fresh = !s;
    if (!s) seen.set(id, (s = blank()));
    const now = w.time;

    // ── steps: back / side / forward / air dash / สลัดหลุด ──
    const st = move?.latest(id);
    if (st && st.at !== s.stepAt) {
      if (!fresh && now - st.at < 0.3 && move?.inBout(id)) c.play(STEP_ID[st.kind], e);
      s.stepAt = st.at;
    }

    // ── dive (ท่าโฉบ) and its landing ──
    const d = air?.dive(id) ?? null;
    if (d && !s.dive) c.play('duel.dive', e);
    else if (!d && s.dive >= 0.85 && e.z < 3) {
      c.play('duel.dive.land', e);
      s.landedAt = now;
    }
    s.dive = d ? Math.max(d.k, 0.01) : 0;

    // ── slide: start, strike, stop ──
    const sl = slideOf(e) !== null;
    if (sl && !s.slide) c.play('duel.slide', e);
    const striking = slide?.striking(id) ?? false;
    if (striking && !s.striking) c.play('duel.slide.strike', e);
    if (!sl && s.slide) {
      const end = slide?.lastEnd(id);
      const how = end && now - end.at < 0.3 ? end.how : 'ran-out';
      if (how === 'ran-out' && !striking) c.play('duel.slide.stop', e);
    }
    s.slide = sl;
    s.striking = striking;

    // ── knock: fling / trip / down / get-up ──
    const kv = knock?.view(id) ?? null;
    const ph = kv?.phase ?? '';
    if (ph !== s.knock) {
      const sid = KNOCK_ID[ph];
      if (sid) c.play(sid, e);
      s.knock = ph;
    }

    // ── armour: the blow soaked (red / yellow) and the yellow shell breaking ──
    const tier = armor?.tier(id) ?? null;
    const soak = armor?.soakedAt.get(id) ?? -1;
    const brk = armor?.brokeAt.get(id) ?? -1;
    if (!fresh && brk !== s.armBreak && brk >= 0) c.play('duel.armor.break', e);
    else if (!fresh && soak !== s.armSoak && soak >= 0) c.play((tier ?? s.tier) === 'yellow' ? 'duel.armor.yellow' : 'duel.armor.red', e);
    s.armSoak = soak;
    s.armBreak = brk;
    if (tier) s.tier = tier;

    // ── rage bar full (ท่าใหญ่ ready) ──
    const full = e.maxRage > 0 && e.rage >= e.maxRage * 0.999;
    if (full && !s.rageFull && !fresh) c.play('duel.rageFull', e, e === p ? {} : { intensity: 0.5 });
    s.rageFull = full;

    // ── lock routine (ท่ายาว): each beat's own sound, and the break-out (F+Q) ──
    const f = lock?.formInfo(id) ?? null;
    if (f) {
      const rt = lockRoutineOf(f.skillId);
      if (rt) {
        let i = 0;
        while (i + 1 < rt.r.beats.length && f.t >= rt.r.beats[i + 1]!.at) i++;
        if (i !== s.beat) {
          if (s.beat < i || s.beat < 0) {
            for (let j = Math.max(0, s.beat + 1); j <= i; j++) {
              const key = rt.r.beats[j]!.sfx;
              if (key === 'sheatheClick') c.play('combat.sheathe', e);
              else if (key) c.play(`duel.lock.${key}`, e);
            }
          }
          s.beat = i;
        }
      }
      s.formLeft = f.len - f.t;
      s.formLen = f.len;
    } else {
      // Gone before its length was up: someone broke out (F+Q) or the form was cut.
      if (s.formLen > 0 && s.formLeft > 0.25) c.play('duel.lock.escape', e);
      s.beat = -1;
      s.formLeft = 0;
      s.formLen = 0;
    }
  }
}
